/* eslint-disable no-async-promise-executor */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { get } from 'lodash';
import Cookies from 'js-cookie';
import { signOut } from '../utility/services/authActions';
import { APP_STORE } from '../utility/store/redux';
import { objectToQueryString } from '../utility/services/api/axios';
import { recoverFromLookerAuthError, refreshLookerAuthToken } from '../utility/services/api/lookerTokenRefresh';
import { SUBS_SCHEMA } from '../config/auth';
import { AbortController } from 'node-abort-controller';

class restApi {
  private axios: AxiosInstance;
  private controllers: Map<string, AbortController>;

  constructor() {
    this.axios = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
      withCredentials: true,
      // headers: {
      //   'Content-type': 'application/json',
      // },
    });
    this.controllers = new Map();

    this.axios.interceptors.request.use(async (config) => {
      const isLookerAuthReplay = Boolean(
        (config as typeof config & { __lookerAuthRetried?: boolean }).__lookerAuthRetried
      );
      const replayToken = config.headers?.['auth_token'];
      let looker_auth_token =
        isLookerAuthReplay && typeof replayToken === 'string' ? replayToken : Cookies.get('user_auth_token');

      // Check server expiration time instead of just cookie existence
      const serverExpirationTime = localStorage.getItem('user_auth_token_expiration');
      const isExpiredOnServer = !serverExpirationTime || Date.now() + 300000 >= parseInt(serverExpirationTime); // 5 min buffer

      if (!isLookerAuthReplay && (!looker_auth_token || looker_auth_token === 'undefined' || isExpiredOnServer)) {
        looker_auth_token = await this.refreshAccessToken();
      }

      if (!config.headers) {
        config.headers = new axios.AxiosHeaders();
      }

      config.headers['auth_token'] = looker_auth_token;

      // AUTH0_TODO Q2022-3777 - Temporary Solution to send sub token to BE, this token should be added in user session
      const subAuthToken = localStorage.getItem('subscriber-auth-token');

      if (subAuthToken) {
        config.headers['subscriber-auth-token'] = subAuthToken;
      }

      const store = APP_STORE;
      if (
        store.getState().root.customerInfo &&
        store.getState().root.customerInfo.length &&
        typeof config?.url == 'string' &&
        ['summary_panel', 'usability_recommendation_list_data'].includes(config?.url) &&
        config?.data?.selected_schema
      ) {
        const custInfo = store.getState().root.customerInfo.find((i) => i.cust_code === config?.data?.selected_schema);
        delete config.data.selected_schema;
        if (get(custInfo, 'additional_user_info.isDemoUser') && get(custInfo, 'additional_user_info.replaceKeywords')) {
          config.headers['isdemouser'] = true;
          config.headers['sandbox-env-replacements'] = btoa(
            JSON.stringify(get(custInfo, 'additional_user_info.replaceKeywords'))
          );
        }
      } else {
        config.headers['checkforsandboxenv'] = true;
      }

      // TODO check after Q2022-3235 is in production, if this `if` statement can be removed:
      if (config.url?.startsWith(process.env.NEXT_PUBLIC_QUATTR_API_URL as string)) {
        let queryString = '';
        if (config.params) {
          queryString = objectToQueryString(config.params);
          config.params = {};
        }
        config.url = `${process.env.NEXT_PUBLIC_HOST_URL}/api/qwa-backend?url=${encodeURIComponent(
          `${config.url}${queryString ? `?${queryString}` : ``}`
        )}`;
      }
      // config.headers['Content-Type'] = 'application/json';
      return config;
    });

    this.axios.interceptors.response.use(undefined, async (error) => {
      // retry if request is timed out and is a GET request
      if (
        error &&
        error.config &&
        error.response &&
        error.response.status === 504 &&
        error.config.method?.toLowerCase() === 'get' // Check if the request method is GET
      ) {
        // retry only one time, set header to identify retried request
        if (!error.config.headers.is_retry) {
          error.config.headers.is_retry = 'true';
          return this.axios.request(error.config);
        }
      }
      if (
        error &&
        error.config &&
        error.response &&
        (error.response?.status === 401 || get(error, 'response.data.error.err.status') === 401)
      ) {
        if (error.response?.status === 401) {
          return recoverFromLookerAuthError(
            error,
            (config) => this.axios.request(config as AxiosRequestConfig),
            () => signOut()
          );
        }

        // Preserve the existing logout behavior for non-Looker nested 401s.
        await signOut();
        return Promise.reject(error);
      }

      // if we have already retried enough times, throw the error up
      throw error;
    });
  }

  private createAbortController(url: string) {
    // If a previous request exists for the same URL, cancel it
    if (this.controllers.has(url)) {
      this.controllers.get(url)?.abort();
    }
    // Create a new AbortController for the request
    const controller = new AbortController();
    this.controllers.set(url, controller); // Store it for later use
    return controller;
  }

  get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T | any> => {
    const controller = this.createAbortController(url); // Create or replace AbortController
    try {
      const response = await this.axios.get<T>(url, config);
      return response.data;
    } catch (err) {
      console.error('REST API Service : get error', err);
      throw err;
    }
  };

  postAbort = async <T = any, P = any>(url: string, payload: P): Promise<T | any> => {
    const controller = this.createAbortController(url);
    try {
      const response = await this.axios.post<T>(url, payload, { signal: controller.signal });
      return response.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.warn('POST | Request canceled:', url);
        return { isCanceled: true }; // Return a clear canceled marker
      } else {
        console.error('REST API Service: post error', JSON.stringify(err, null, 2), err);
        throw err;
      }
    }
  };

  post = async <T = any, P = any>(url: string, payload: P): Promise<T | any> => {
    try {
      const response = await this.axios.post<T>(url, payload);
      return response.data;
    } catch (err) {
      console.error('REST API Service: post error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  postFormData = async <T = any>(url: string, formData: FormData, headers: Record<string, string> = {}): Promise<T> => {
    try {
      const response = await this.axios.post<T>(url, formData, {
        headers: {
          ...headers,
          // Let axios set multipart boundary automatically when FormData is provided
        },
      });
      return response.data as T;
    } catch (err) {
      console.error('REST API Service: postFormData error', err);
      throw err;
    }
  };

  cancelRequest = (url: string) => {
    if (this.controllers.has(url)) {
      this.controllers.get(url)?.abort();
      this.controllers.delete(url);
      console.log(`cancelRequest | Request to ${url} canceled.`);
    }
  };

  cancelAllRequests = () => {
    this.controllers.forEach((controller, url) => {
      controller.abort(); // Abort each tracked request
      console.log(`Canceled request: ${url}`);
    });
    this.controllers.clear(); // Clear all stored controllers
  };

  delete = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.delete(url, { data: payload });
      return response.data;
    } catch (err) {
      console.error('REST API Service : delete error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  put = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.put<T>(url, payload);
      return response.data;
    } catch (err) {
      console.error('REST API Service : put error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  patch = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.patch<T>(url, payload);
      return response.data;
    } catch (err) {
      console.error('REST API Service : patch error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  /**
   * Delegates to the SHARED refresh (see lookerTokenRefresh.ts). This class
   * used to run its own de-dupe flag, independent of the one in
   * utility/services/api/axios.ts, so both clients could re-mint a Looker token
   * concurrently and race each other. One shared in-flight promise removes that.
   */
  private async refreshAccessToken(): Promise<string> {
    return refreshLookerAuthToken();
  }
}

export default new restApi();
